import React from 'react';
import { Stack } from '@mui/material';

import { AbsoluteLine, Direction2D, Line2D, Point2D } from '@paragon/contract-library';
import { degreesToVector, vectorToDegrees } from '@paragon/geometry-library';
import { AngleField, DistanceField } from '@paragon/material';

import { findAndSetReferenceLine } from '../../../../store/actions/editorActions';
import { getAsyncRequestForActiveComponentDesign } from '../../../../store/selectors/getAsyncRequestForActiveComponentDesignSelector';
import { propertiesShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import ReferenceLineNameProperty from '../elements/ReferenceLineNameProperty';
import { ReferenceLineTypeProperty } from '../elements/ReferenceLineTypeProperty';

interface Props {
  line: AbsoluteLine;
  afterUpdateLine?: () => void;
}

const AbsoluteLineProperties = ({ line, afterUpdateLine }: Props) => {
  const dispatch = useAppDispatch();
  const disabled = useAppSelector((state) => {
    const asyncRequest = getAsyncRequestForActiveComponentDesign(state);
    return propertiesShouldBeViewOnly(state) || (!!asyncRequest && asyncRequest.type !== 'updateFeatures');
  });

  const updateLine = (newLine: Line2D) => {
    const newAbsoluteLine = { ...line, line: newLine };
    dispatch(findAndSetReferenceLine(newAbsoluteLine));
    afterUpdateLine && afterUpdateLine();
  };

  const updateBasePoint = (newBasePoint: Point2D) => updateLine({ ...line.line, basePoint: newBasePoint });
  const onApplyBasePointX = (newX: number) => updateBasePoint({ ...line.line.basePoint, x: newX });
  const onApplyBasePointY = (newY: number) => updateBasePoint({ ...line.line.basePoint, y: newY });

  const updateDirection = (newDirection: Direction2D) => updateLine({ ...line.line, direction: newDirection });

  return (
    <Stack direction="column" spacing={2}>
      <ReferenceLineNameProperty sx={{ marginBottom: '0!important' }} line={line} disabled={disabled} />
      <ReferenceLineTypeProperty line={line} disabled={disabled} />

      <Stack direction="row" spacing={1}>
        <DistanceField value={line.line.basePoint.x} label="Base X" onSubmit={onApplyBasePointX} disabled={disabled} />
        <DistanceField value={line.line.basePoint.y} label="Base Y" onSubmit={onApplyBasePointY} disabled={disabled} />
      </Stack>

      <AngleField
        value={vectorToDegrees(line.line.direction)}
        label="Line Direction"
        disabled={disabled}
        onSubmit={(newDirection) => updateDirection(degreesToVector(newDirection))}
      />
    </Stack>
  );
};

export default AbsoluteLineProperties;
